import { useState, type FormEvent } from 'react'
import { Loader2, ChevronDown, Check } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import Logo from '@/components/Logo'
import AuthBackgroundShape from '@/components/AuthBackgroundShape'
import { useCreateOrganization } from '@/data/hooks/api'
import { useAuth } from '@/hooks/useAuth'

const ORGANIZATION_SIZES = [
  { value: 'just_me', label: 'Just me' },
  { value: '2-10', label: '2-10' },
  { value: '11-50', label: '11-50' },
  { value: '51-200', label: '51-200' },
  { value: '201-1000', label: '201-1000' },
  { value: '1000+', label: '1000+' },
]

const USE_CASES = [
  { value: 'monitor_costs', label: 'Monitor AI costs' },
  { value: 'optimize_tokens', label: 'Optimize token usage' },
  { value: 'track_customer_usage', label: 'Track customer usage' },
  { value: 'budget_management', label: 'Budget management' },
  { value: 'multi_provider_comparison', label: 'Multi-provider comparison' },
  { value: 'team_cost_allocation', label: 'Team cost allocation' },
  { value: 'other', label: 'Other' },
]

const REFERRAL_SOURCES = [
  { value: 'search_engine', label: 'Search engine (Google, etc.)' },
  { value: 'social_media', label: 'Social media (Twitter, LinkedIn, etc.)' },
  { value: 'friend_colleague', label: 'Friend or colleague' },
  { value: 'blog_article', label: 'Blog post or article' },
  { value: 'product_hunt', label: 'Product Hunt' },
  { value: 'github', label: 'GitHub' },
  { value: 'other', label: 'Other' },
]

const ROLES = [
  { value: 'developer', label: 'Developer/Engineer' },
  { value: 'engineering_manager', label: 'Engineering Manager' },
  { value: 'product_manager', label: 'Product Manager' },
  { value: 'cto_vp', label: 'CTO/VP Engineering' },
  { value: 'founder_ceo', label: 'Founder/CEO' },
  { value: 'other', label: 'Other' },
]

// Use Case MultiSelect Component
interface UseCaseMultiSelectProps {
  value: string[]
  onChange: (value: string) => void
  disabled?: boolean
}

function UseCaseMultiSelect({ value, onChange, disabled }: UseCaseMultiSelectProps) {
  const [open, setOpen] = useState(false)
  const selectedCount = value.length
  const isAllSelected = value.length === USE_CASES.length

  const getDisplayText = () => {
    if (selectedCount === 0) return 'Select use cases'
    if (isAllSelected) return 'All use cases selected'
    return `${selectedCount} use case${selectedCount === 1 ? '' : 's'} selected`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          disabled={disabled}
          className='w-full justify-between'
        >
          <span className={cn('truncate', selectedCount === 0 && 'text-muted-foreground')}>
            {getDisplayText()}
          </span>
          <ChevronDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[var(--radix-popover-trigger-width)] p-0' align='start'>
        <div className='max-h-[300px] overflow-y-auto'>
          {USE_CASES.map(useCase => {
            const isSelected = value.includes(useCase.value)
            return (
              <div
                key={useCase.value}
                className='flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer'
                onClick={() => onChange(useCase.value)}
              >
                <Checkbox
                  checked={isSelected}
                  className='pointer-events-none'
                />
                <span className='text-sm'>{useCase.label}</span>
                {isSelected && (
                  <Check className='ml-auto h-4 w-4 text-primary' />
                )}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface FormData {
  organizationName: string
  organizationSize: string
  primaryUseCase: string[]
  referralSource: string
  userRole: string
}

const OrganizationSetupPage = () => {
  const { checkAuth } = useAuth()
  const createOrganization = useCreateOrganization()
  const [formData, setFormData] = useState<FormData>({
    organizationName: '',
    organizationSize: '',
    primaryUseCase: [],
    referralSource: '',
    userRole: '',
  })

  const isFormValid = formData.organizationName.trim().length >= 2

  const handleInputChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSelectChange = (field: keyof FormData) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleUseCaseToggle = (value: string) => {
    setFormData(prev => {
      const currentUseCases = prev.primaryUseCase
      if (currentUseCases.includes(value)) {
        return { ...prev, primaryUseCase: currentUseCases.filter(v => v !== value) }
      } else {
        return { ...prev, primaryUseCase: [...currentUseCases, value] }
      }
    })
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!isFormValid) {
      toast.error('Organization name must be at least 2 characters')
      return
    }

    try {
      const onboardingData: Record<string, any> = {}
      
      if (formData.organizationSize) {
        onboardingData.organization_size = formData.organizationSize
      }
      if (formData.primaryUseCase.length > 0) {
        onboardingData.primary_use_case = formData.primaryUseCase
      }
      if (formData.referralSource) {
        onboardingData.referral_source = formData.referralSource
      }
      if (formData.userRole) {
        onboardingData.user_role = formData.userRole
      }

      await createOrganization.mutateAsync({
        name: formData.organizationName.trim(),
        onboarding_data: Object.keys(onboardingData).length > 0 ? onboardingData : undefined,
      })
      await checkAuth()
    } catch (error) {
      console.error('Failed to setup organization:', error)
      toast.error('Failed to setup organization. Please try again.')
    }
  }

  return (
    <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
      <div className='absolute'>
        <AuthBackgroundShape />
      </div>

      <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
        <CardHeader className='gap-6'>
          <Logo className='gap-3 mx-auto' showText={false} />

          <div className='text-center'>
            <CardTitle className='mb-1.5 text-2xl'>Set up your organization</CardTitle>
            <CardDescription className='text-base'>
              Help us customize your experience and get you started quickly.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className='space-y-5'>
            {/* Organization Name - Required */}
            <div className='space-y-1.5'>
              <Label htmlFor='organizationName' className='leading-5'>
                Organization name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='organizationName'
                type='text'
                placeholder='Acme Corp'
                value={formData.organizationName}
                onChange={handleInputChange('organizationName')}
                disabled={createOrganization.isPending}
                required
                minLength={2}
              />
            </div>

            {/* Organization Size - Optional */}
            <div className='space-y-1.5'>
              <Label htmlFor='organizationSize' className='leading-5'>
                How many people are in your organization?{' '}
                <span className='text-muted-foreground font-normal'>(Optional)</span>
              </Label>
              <Select
                value={formData.organizationSize}
                onValueChange={handleSelectChange('organizationSize')}
                disabled={createOrganization.isPending}
              >
                <SelectTrigger id='organizationSize' className='w-full'>
                  <SelectValue placeholder='Select size' />
                </SelectTrigger>
                <SelectContent>
                  {ORGANIZATION_SIZES.map(size => (
                    <SelectItem key={size.value} value={size.value}>
                      {size.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Primary Use Case - Optional Multiselect */}
            <div className='space-y-1.5'>
              <Label htmlFor='primaryUseCase' className='leading-5'>
                What will you use Fenra for?{' '}
                <span className='text-muted-foreground font-normal'>(Optional)</span>
              </Label>
              <UseCaseMultiSelect
                value={formData.primaryUseCase}
                onChange={handleUseCaseToggle}
                disabled={createOrganization.isPending}
              />
            </div>

            {/* Referral Source - Optional */}
            <div className='space-y-1.5'>
              <Label htmlFor='referralSource' className='leading-5'>
                How did you hear about us?{' '}
                <span className='text-muted-foreground font-normal'>(Optional)</span>
              </Label>
              <Select
                value={formData.referralSource}
                onValueChange={handleSelectChange('referralSource')}
                disabled={createOrganization.isPending}
              >
                <SelectTrigger id='referralSource' className='w-full'>
                  <SelectValue placeholder='Select source' />
                </SelectTrigger>
                <SelectContent>
                  {REFERRAL_SOURCES.map(source => (
                    <SelectItem key={source.value} value={source.value}>
                      {source.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* User Role - Optional */}
            <div className='space-y-1.5'>
              <Label htmlFor='userRole' className='leading-5'>
                What's your role?{' '}
                <span className='text-muted-foreground font-normal'>(Optional)</span>
              </Label>
              <Select
                value={formData.userRole}
                onValueChange={handleSelectChange('userRole')}
                disabled={createOrganization.isPending}
              >
                <SelectTrigger id='userRole' className='w-full'>
                  <SelectValue placeholder='Select your role' />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Buttons */}
            <div className='space-y-3 pt-2'>
              <Button type='submit' className='w-full' disabled={createOrganization.isPending || !isFormValid}>
                {createOrganization.isPending ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Setting up...
                  </>
                ) : (
                  'Get Started'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default OrganizationSetupPage

